import React, { useEffect, useState } from 'react';
import {
  getElections,
  getClientVoteLogs,
  deleteUserVotes
} from '../../core/services/voting';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import _ from 'lodash';
import { mkConfig, generateCsv, download } from 'export-to-csv';

const csvConfig = mkConfig({
  useKeysAsHeaders: true
});

const ClientVoteLogs = () => {
  const [elections, setElections] = useState([]);
  const [selectedVoteLogElection, setSelectedVoteLogElection] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientVoteLogs, setClientVoteLogs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchElections = async () => {
      const res = await getElections();
      setElections(res.elections);
    };
    fetchElections();
  }, []);

  const handleVoteLogElectionChange = event => {
    setSelectedVoteLogElection(event.target.value);
  };

  const handleClientIdChange = event => {
    setClientId(event.target.value);
  };

  const handleFetchClientVoteLogs = async () => {
    setLoading(true);
    const res = await getClientVoteLogs(clientId, selectedVoteLogElection);
    setClientVoteLogs(res.voteLogs);
    setLoading(false);

    const data = res.voteLogs.sort((a, b) => {
      const caster = a.caster_name.localeCompare(b.caster_name);
      if (caster !== 0) {
        return caster;
      }
      return a.vote.localeCompare(b.vote);
    });

    const csv = generateCsv(csvConfig)(data);
    download(csvConfig)(csv);
  };

  const handleDeleteVotes = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    await deleteUserVotes(selectedVoteLogElection, deleteTarget);
    setDeleting(false);
    setDeleteTarget(null);
    // Refresh the table
    const res = await getClientVoteLogs(clientId, selectedVoteLogElection);
    setClientVoteLogs(res.voteLogs);
  };

  // Group logs by caster_name to show one reset button per user
  const uniqueCasters = clientVoteLogs
    ? [...new Set(clientVoteLogs.map(log => log.caster_name))]
    : [];

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto', padding: 2 }}>
      <Typography variant="h6" gutterBottom>
        Fetch Client Vote Logs
      </Typography>
      <FormControl variant="standard" fullWidth sx={{ marginBottom: 2 }}>
        <InputLabel id="select-vote-log-election-label">
          Select Election
        </InputLabel>
        <Select
          labelId="select-vote-log-election-label"
          value={selectedVoteLogElection}
          onChange={handleVoteLogElectionChange}
        >
          {elections.map(election => (
            <MenuItem key={election.id} value={election.id}>
              {election.title}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Client ID"
        variant="standard"
        fullWidth
        value={clientId}
        onChange={handleClientIdChange}
        sx={{ marginBottom: 2 }}
      />
      <Button
        variant="contained"
        color="primary"
        onClick={handleFetchClientVoteLogs}
        disabled={!selectedVoteLogElection || loading}
      >
        {loading ? <CircularProgress size={24} /> : 'Fetch Vote Logs'}
      </Button>

      {clientVoteLogs && clientVoteLogs.length > 0 && (
        <>
          {/* Reset Votes Section */}
          <Box sx={{ marginTop: 2, marginBottom: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Reset Votes
            </Typography>
            {uniqueCasters.map(caster => (
              <Button
                key={caster}
                variant="outlined"
                color="error"
                size="small"
                sx={{ marginRight: 1, marginBottom: 1 }}
                onClick={() => setDeleteTarget(caster)}
              >
                Reset: {caster}
              </Button>
            ))}
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 'none',
              borderRadius: 0,
              border: '1px solid #ccc',
              marginTop: 2
            }}
          >
            <Table sx={{ borderCollapse: 'collapse' }}>
              <TableBody>
                <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                  <TableCell
                    sx={{ border: '1px solid #ccc', fontWeight: 'bold' }}
                  >
                    #
                  </TableCell>
                  <TableCell
                    sx={{ border: '1px solid #ccc', fontWeight: 'bold' }}
                  >
                    Client ID
                  </TableCell>
                  <TableCell
                    sx={{ border: '1px solid #ccc', fontWeight: 'bold' }}
                  >
                    Client Name
                  </TableCell>
                  <TableCell
                    sx={{ border: '1px solid #ccc', fontWeight: 'bold' }}
                  >
                    Vote/Poll
                  </TableCell>
                  <TableCell
                    sx={{ border: '1px solid #ccc', fontWeight: 'bold' }}
                  >
                    Answer
                  </TableCell>
                </TableRow>
                {clientVoteLogs
                  .sort((a, b) => {
                    const caster = a.caster_name.localeCompare(b.caster_name);
                    if (caster !== 0) {
                      return caster;
                    }
                    return a.vote.localeCompare(b.vote);
                  })
                  .map((log, index) => (
                    <React.Fragment key={_.toString(index)}>
                      <TableRow key={index}>
                        <TableCell sx={{ border: '1px solid #ccc' }}>
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #ccc' }}>
                          {log.client_id}
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #ccc' }}>
                          {log.caster_name}
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #ccc' }}>
                          {log.vote}
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #ccc' }}>
                          {log.answer}
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {clientVoteLogs && clientVoteLogs.length === 0 && (
        <Typography sx={{ marginTop: 2 }} color="text.secondary">
          No vote logs found.
        </Typography>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Reset Votes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all votes for{' '}
            <strong>{deleteTarget}</strong>? This will allow them to vote again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteVotes}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete Votes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientVoteLogs;
